import React, { useState, useMemo } from 'react';
import { offerToSavePassword } from '../utils/savePassword';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import { Button } from '../components/ui/button';
import {
  BookOpen,
  Mail,
  Lock,
  User,
  UserCircle,
  Eye,
  EyeOff,
  ArrowLeft,
  ShieldCheck,
  Loader2,
  Sparkles,
  BadgeCheck,
  Video,
  MessageCircle,
  Mic,
  Star,
  Globe2,
} from 'lucide-react';
import { toast } from 'sonner';
import Logo, { LogoPicture } from '../components/Logo';
import { authAPI } from '../api';
import { Squiggle, Sparkle4, HeroBlob } from '../components/BrandDecor';
import { dashboardPath } from '../utils/dashboard';
import '../styles/page-motion.css';
import MotionFX from '../components/MotionFX';
import Typewriter from '../components/Typewriter';

const TYPED = ['Speak with confidence.', 'Learn at your own pace.', 'Classes in your time zone.', 'Real teachers, live 1-on-1.'];

/* Right-side brand panel, shared across login/signup/forgot screens —
   mirrors the landing hero: shield on a blue blob, two-tone headline. */
const HeroPanel = () => {
  const { t } = useTranslation();
  return (
    <div className="hidden lg:flex relative flex-1 p-6">
      <div className="relative flex-1 liquid-glass rounded-[2rem] bg-white/60 dark:bg-transparent overflow-hidden flex flex-col justify-between p-12">
        <div className="auth-orb auth-orb-a w-64 h-64 bg-primary/40 -top-16 -right-10" aria-hidden="true" />
        <div className="auth-orb auth-orb-b w-72 h-72 bg-gold-400/30 bottom-0 -left-16" aria-hidden="true" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-white/60 dark:bg-white/5 text-primary dark:text-primary-300 text-xs font-semibold uppercase tracking-wider px-4 py-1.5 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-gold-500" />
            Live 1-on-1 English lessons, online
          </div>
          <h2 className="text-4xl xl:text-5xl font-heading font-bold tracking-tight leading-[1.08] max-w-lg">
            <span className="auth-line text-navy dark:text-white">Learn. Speak.</span>
            <span className="auth-line bg-gradient-to-r from-primary via-primary-500 to-primary-700 dark:from-primary-300 dark:via-primary-200 dark:to-gold-300 bg-clip-text text-transparent pb-1">
              {t('landing.hero_title')}
            </span>
          </h2>
          <p className="auth-line mt-4 text-lg font-semibold text-gold-700 dark:text-gold-300 min-h-[1.75rem]">
            <Typewriter phrases={TYPED} />
          </p>
        </div>

        <div className="relative mx-auto w-full max-w-md flex items-center justify-center min-h-[320px] xl:min-h-[380px]">
          <HeroBlob />
          <div className="absolute w-56 h-56 rounded-full bg-gold-300/50 blur-3xl" aria-hidden="true" />
          <div className="auth-orbit w-[19rem] h-[19rem] xl:w-[22rem] xl:h-[22rem]" aria-hidden="true">
            <i><MessageCircle className="w-4 h-4" /></i>
            <i style={{ top: 'auto', bottom: -14 }}><Star className="w-4 h-4" /></i>
          </div>
          <div className="auth-orbit auth-orbit-rev w-[25rem] h-[25rem] xl:w-[28rem] xl:h-[28rem] opacity-70" aria-hidden="true">
            <i style={{ top: 'calc(50% - 14px)', left: -14 }}><Mic className="w-4 h-4" /></i>
            <i style={{ top: 'calc(50% - 14px)', left: 'auto', right: -14 }}><Globe2 className="w-4 h-4" /></i>
          </div>
          <Squiggle className="absolute top-0 left-0 w-24 text-navy/70 dark:text-white/50" />
          <Sparkle4 className="auth-twinkle absolute bottom-6 right-[18%] w-7 h-7 text-navy/70 dark:text-gold-300" />
          <Sparkle4 className="auth-twinkle absolute top-8 right-[22%] w-4 h-4 text-gold-500" />
          {[['Hello! 👋', 'top-[8%] left-[2%]', 0], ['Kumusta? 😊', 'top-[12%] right-[0%]', 2.5], ['Nice to meet you!', 'bottom-[14%] left-[0%]', 5], ["Let's practice! 🎯", 'bottom-[8%] right-[4%]', 7.5]].map(([text, pos, delay]) => (
            <span key={text} className={`auth-bubble ${pos} z-20 rounded-2xl rounded-bl-sm bg-white dark:bg-slate-800 text-navy dark:text-white text-sm font-semibold px-3 py-2 shadow-card-hover border border-primary/10 dark:border-white/10`}
              style={{ animationDelay: `${delay}s` }} aria-hidden="true">{text}</span>
          ))}
          <LogoPicture
            large
            className="auth-float relative w-56 xl:w-64 h-auto drop-shadow-[0_24px_40px_rgba(0,28,80,0.35)]"
          />
        </div>

        <div className="auth-trust flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {[
            { icon: BadgeCheck, label: 'Verified tutors' },
            { icon: Video, label: 'Live HD video lessons' },
            { icon: Lock, label: 'Secure payments' }
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-navy dark:text-white font-semibold text-sm">
              <Icon className="w-5 h-5 text-gold-500" />
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* Letters and words floating up behind the form (every screen size). */
const LETTERS = [['A', 6, 34, 0], ['Hi!', 18, 22, -8], ['B', 30, 28, -15], ['?', 46, 40, -4], ['abc', 58, 20, -19], ['C', 70, 30, -11], ['✓', 82, 26, -2], ['Hello', 92, 18, -16]];
const AuthBackdrop = () => (
  <div className="auth-backdrop" aria-hidden="true">
    <div className="auth-orb auth-orb-a w-72 h-72 bg-primary/25 -top-20 -left-20" />
    <div className="auth-orb auth-orb-b w-64 h-64 bg-gold-400/20 bottom-10 right-0 lg:right-1/2" />
    {LETTERS.map(([text, left, size, delay]) => (
      <span key={text} className="auth-letter" style={{ left: `${left}%`, fontSize: size, animationDelay: `${delay}s` }}>{text}</span>
    ))}
  </div>
);

/* Mobile-only slim brand banner shown above the form. */
const MobileImageBanner = () => (
  <div className="lg:hidden relative px-5 pt-6 flex items-center gap-4">
    <LogoPicture alt="" className="w-16 h-auto drop-shadow-lg" aria-hidden="true" />
    <p className="text-2xl font-heading font-bold leading-tight tracking-tight">
      <span className="text-navy dark:text-white">Learn. Speak. </span>
      <span className="text-primary dark:text-primary-300">Grow with Bridgeway.</span>
    </p>
  </div>
);

/*
 * Redesigned premium AuthPage — Feb 2026.
 *
 * Preserves ALL existing auth logic:
 *   - login({email, password}) → role-based dashboard redirect
 *   - register({...formData, role}) → same redirect
 *   - forgot-password flow with authAPI.forgotPassword
 *   - Account lockout (429) handling
 *   - i18n translation keys (unchanged)
 *
 * New UX:
 *   - Two-column 52/48 desktop layout, image collapses on mobile to a slim
 *     header banner instead of hiding entirely
 *   - Persistent labels with icon-prefixed inputs, proper autocomplete attrs
 *   - Inline required-field validation → Sign In button disabled until valid
 *   - Remember-me checkbox (client-side; consumed by AuthContext localStorage)
 *   - Loading spinner inside the submit button
 *   - "Secure sign-in" trust indicator
 *   - Dark-mode aware via existing CSS variables
 */

/* Small helper — icon-prefixed text input with focus ring + optional trailing action */
const IconInput = ({
  id,
  name,
  type = 'text',
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  icon: Icon,
  autoComplete,
  required = true,
  error,
  trailing,
  testId,
}) => {
  const showError = Boolean(error);
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="text-sm font-medium text-text-primary block"
      >
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <Icon
            className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] transition-colors ${
              showError ? 'text-red-500' : 'text-text-muted'
            }`}
            aria-hidden="true"
          />
        )}
        <input
          id={id}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          autoComplete={autoComplete}
          data-testid={testId}
          aria-invalid={showError}
          aria-describedby={showError ? `${id}-error` : undefined}
          className={`w-full h-12 pl-11 ${trailing ? 'pr-11' : 'pr-4'} rounded-xl border bg-white dark:bg-slate-800/60 text-text-primary placeholder:text-text-muted text-[15px] transition-all duration-200 outline-none focus:ring-4 ${
            showError
              ? 'border-red-400 focus:border-red-500 focus:ring-red-100 dark:focus:ring-red-500/20'
              : 'border-slate-200 dark:border-slate-700 focus:border-primary focus:ring-primary/15 dark:focus:ring-primary/25 hover:border-slate-300 dark:hover:border-slate-600'
          }`}
        />
        {trailing && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            {trailing}
          </div>
        )}
      </div>
      {showError && (
        <p
          id={`${id}-error`}
          role="alert"
          className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1 pt-0.5"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
};

const AuthPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'login';
  const [mode, setMode] = useState(initialMode);
  const [role, setRole] = useState('student');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
  });
  const [touched, setTouched] = useState({});
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Page the user was trying to open before being sent to log in (in-app paths only).
  const returnTo = typeof location.state?.from === 'string' && location.state.from.startsWith('/') && !location.state.from.startsWith('//')
    ? location.state.from
    : null;
  const { login, register } = useAuth();

  // Ignore stray spaces/capitals from autofill or phone keyboards.
  const cleanEmail = (email) => (email || '').trim().toLowerCase();
  const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(cleanEmail(email));

  const errors = useMemo(() => {
    const e = {};
    if (touched.email) {
      if (!formData.email) e.email = 'Email is required';
      else if (!isEmailValid(formData.email)) e.email = 'Enter a valid email address';
    }
    if (touched.password) {
      if (!formData.password) e.password = 'Password is required';
      else if (mode === 'signup' && formData.password.length < 8) {
        e.password = 'Use at least 8 characters';
      }
    }
    if (mode === 'signup' && touched.full_name && !formData.full_name.trim()) {
      e.full_name = 'Please enter your name';
    }
    return e;
  }, [touched, formData, mode]);

  const isFormValid = useMemo(() => {
    if (!isEmailValid(formData.email)) return false;
    if (!formData.password) return false;
    if (mode === 'signup') {
      if (!formData.full_name.trim()) return false;
      if (formData.password.length < 8) return false;
    }
    return true;
  }, [formData, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true, full_name: true });
    if (!isFormValid) return;

    setLoading(true);
    try {
      if (mode === 'login') {
        // rememberMe is a UI-only preference for now — persisted locally, not sent to backend
        try { localStorage.setItem('bge_remember_me', rememberMe ? '1' : '0'); } catch { /* private mode */ }
        const result = await login({ email: cleanEmail(formData.email), password: formData.password });
        if (rememberMe) {
          await offerToSavePassword({ email: cleanEmail(formData.email), password: formData.password, name: result.user.full_name });
        }
        toast.success('Welcome!');
        navigate(returnTo || dashboardPath(result.user.role));
      } else {
        if (role === 'tutor' && !/@(bge\.com|bridgewayglobalenglish\.com)$/i.test(cleanEmail(formData.email))) {
          toast.error('Teachers must sign up with a work email (@bge.com or @bridgewayglobalenglish.com).');
          setLoading(false);
          return;
        }
        const result = await register({ ...formData, email: cleanEmail(formData.email), role });
        await offerToSavePassword({ email: cleanEmail(formData.email), password: formData.password, name: result.user.full_name });
        toast.success('Account created successfully!');
        navigate(returnTo || dashboardPath(result.user.role));
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(
          error.response?.data?.detail || 'Account temporarily locked. Please try again later.',
          { duration: 8000 }
        );
      } else {
        toast.error(error.response?.data?.detail || 'An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail || !isEmailValid(forgotEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setForgotLoading(true);
    try {
      await authAPI.forgotPassword(cleanEmail(forgotEmail));
      toast.success('If an account with that email exists, a password reset link has been sent.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  // ---------- Forgot Password screen ----------
  if (showForgotPassword) {
    return (
      <div className="min-h-screen relative isolate overflow-hidden flex flex-col lg:flex-row">
        <AuthBackdrop />
      <MotionFX />
        <MobileImageBanner />
        <div className="flex-1 flex items-center justify-center px-5 py-10 sm:p-8 lg:p-12">
          <div className="w-full max-w-[440px]">
            <div className="mb-8 hidden lg:block">
              <Logo size="default" />
            </div>

            <button
              onClick={() => setShowForgotPassword(false)}
              className="inline-flex items-center text-sm text-text-secondary hover:text-primary mb-6 transition-colors"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              {t('auth.back_to_login')}
            </button>

            <div className="auth-card auth-stagger glass-panel rounded-[1.75rem] p-6 sm:p-8 shadow-xl shadow-primary/10 dark:shadow-black/30">
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-navy dark:text-white mb-2 tracking-tight">
                {t('auth.forgot_title')}
              </h1>
              <p className="text-text-secondary mb-7 text-[15px]">{t('auth.forgot_body')}</p>

              <form onSubmit={handleForgotPassword} className="space-y-5">
                <IconInput
                  id="forgot-email"
                  name="forgot-email"
                  type="email"
                  label={t('auth.email_address')}
                  placeholder={t('auth.email_placeholder')}
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  icon={Mail}
                  autoComplete="email"
                  testId="forgot-email-input"
                />

                <Button
                  type="submit"
                  disabled={forgotLoading || !isEmailValid(forgotEmail)}
                  data-testid="send-reset-link-btn"
                  className="w-full h-12 rounded-xl liquid-pill-primary liquid-sheen border-0 font-semibold disabled:opacity-60"
                >
                  {forgotLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('auth.sending')}
                    </>
                  ) : (
                    t('auth.send_reset_link')
                  )}
                </Button>
              </form>
            </div>

            <button
              onClick={() => navigate('/')}
              className="w-full mt-5 text-sm text-text-muted hover:text-text-secondary transition-colors"
              data-testid="back-home-btn"
            >
              {t('auth.back_home')}
            </button>
          </div>
        </div>
        <HeroPanel />
      </div>
    );
  }

  // ---------- Login / Signup screen ----------
  return (
    <div className="min-h-screen relative isolate overflow-hidden flex flex-col lg:flex-row">
      <AuthBackdrop />
      <MotionFX />
      <MobileImageBanner />

      {/* Left — form panel with soft gradient */}
      <div className="flex-1 relative flex items-center justify-center px-5 py-8 sm:p-8 lg:p-12">
        <div className="relative w-full max-w-[440px]">
          <div className="mb-7 hidden lg:block">
            <Logo size="default" />
          </div>

          <div key={mode} className="auth-card auth-stagger glass-panel rounded-[1.75rem] p-6 sm:p-8 shadow-xl shadow-primary/10 dark:shadow-black/30">
            <h1
              className="text-2xl sm:text-[28px] font-heading font-bold text-navy dark:text-white tracking-tight leading-tight mb-2"
              data-testid="auth-title"
            >
              <span className="auth-title-shine">{mode === 'login' ? 'Welcome' : 'Create your account'}</span>
              {mode === 'login' && <span className="auth-wave ml-2" aria-hidden="true">👋</span>}
            </h1>
            <p className="text-text-secondary text-[15px] mb-6">
              {mode === 'login'
                ? 'Continue your journey toward confident English communication.'
                : 'Join thousands of learners connecting with expert tutors.'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {mode === 'signup' && (
                <>
                  <IconInput
                    id="full_name"
                    name="full_name"
                    type="text"
                    label={t('auth.full_name')}
                    placeholder={t('auth.full_name_placeholder')}
                    value={formData.full_name}
                    onChange={handleInputChange}
                    onBlur={() => markTouched('full_name')}
                    icon={User}
                    autoComplete="name"
                    error={errors.full_name}
                    testId="fullname-input"
                  />

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-text-primary block">
                      {t('auth.i_am_a')}
                    </label>
                    <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label={t('auth.i_am_a')}>
                      <button
                        type="button"
                        data-testid="role-student-btn"
                        role="radio"
                        aria-checked={role === 'student'}
                        onClick={() => setRole('student')}
                        className={`p-3.5 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-1.5 ${
                          role === 'student'
                            ? 'border-primary bg-primary/5 text-primary shadow-sm'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-text-secondary'
                        }`}
                      >
                        <UserCircle className="w-5 h-5" />
                        <span className="font-medium text-sm">{t('auth.student')}</span>
                      </button>
                      <button
                        type="button"
                        data-testid="role-tutor-btn"
                        role="radio"
                        aria-checked={role === 'tutor'}
                        onClick={() => setRole('tutor')}
                        className={`p-3.5 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-1.5 ${
                          role === 'tutor'
                            ? 'border-primary bg-primary/5 text-primary shadow-sm'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-text-secondary'
                        }`}
                      >
                        <BookOpen className="w-5 h-5" />
                        <span className="font-medium text-sm">{t('auth.tutor')}</span>
                      </button>
                    </div>
                    {role === 'tutor' && (
                      <p className="text-xs text-text-secondary mt-2" data-testid="tutor-work-email-hint">
                        Teachers sign up with their work email (@bge.com or @bridgewayglobalenglish.com).
                      </p>
                    )}
                  </div>
                </>
              )}

              <IconInput
                id="email"
                name="email"
                type="email"
                label="Email address"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={() => markTouched('email')}
                icon={Mail}
                autoComplete={mode === 'signup' ? 'email' : 'username'}
                error={errors.email}
                testId="email-input"
              />

              <IconInput
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                placeholder={mode === 'signup' ? 'At least 8 characters' : '••••••••'}
                value={formData.password}
                onChange={handleInputChange}
                onBlur={() => markTouched('password')}
                icon={Lock}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                error={errors.password}
                testId="password-input"
                trailing={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    data-testid="toggle-password-visibility"
                    aria-label={showPassword ? t('auth.hide_password') : t('auth.show_password')}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
              />

              {mode === 'login' && (
                <div className="flex items-center justify-between pt-1">
                  <label className="relative flex items-center gap-2 cursor-pointer group select-none">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="peer appearance-none w-4 h-4 rounded border-2 border-slate-300 dark:border-slate-500 bg-transparent checked:bg-primary checked:border-primary dark:checked:bg-primary-400 dark:checked:border-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 cursor-pointer transition-colors"
                      data-testid="remember-me-checkbox"
                    />
                    <svg
                      viewBox="0 0 16 16"
                      className="absolute left-0 w-4 h-4 text-white pointer-events-none opacity-0 peer-checked:opacity-100"
                      aria-hidden="true"
                    >
                      <path d="M4 8.5l2.5 2.5L12 5.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
                      Remember me
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs font-medium text-primary hover:text-primary/80 hover:underline underline-offset-2 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    {t('auth.forgot_password')}
                  </button>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !isFormValid}
                data-testid="auth-submit-btn"
                className="auth-btn-shine w-full h-12 mt-2 rounded-xl liquid-pill-primary liquid-sheen border-0 font-semibold text-[15px] disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.please_wait')}
                  </>
                ) : mode === 'login' ? (
                  t('auth.sign_in')
                ) : (
                  t('auth.create_account')
                )}
              </Button>

              {/* Secure sign-in trust indicator */}
              <div className="flex items-center justify-center gap-1.5 text-xs text-text-muted pt-1">
                <ShieldCheck className="w-3.5 h-3.5 text-primary dark:text-primary-300" />
                <span>Secure sign-in · SSL encrypted</span>
              </div>
            </form>

            {/* Divider — separates password auth from social auth */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 border-t border-slate-200 dark:border-slate-700" />
              <span className="text-xs text-text-muted uppercase tracking-wider">
                Or continue with
              </span>
              <div className="flex-1 border-t border-slate-200 dark:border-slate-700" />
            </div>

            {/* Google Sign-In (Emergent-managed OAuth)
                REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              onClick={() => {
                // Standard Google OAuth 2.0 flow — backend redirects to Google, then back to /auth#google_token=<jwt>.
                // If GOOGLE_OAUTH_ENABLED is false on the backend, the buyer sees a 503 with a clear reason.
                window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/authorize`;
              }}
              className="w-full h-11 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center justify-center gap-3 text-sm font-medium text-text-primary transition-all duration-200"
              data-testid="google-signin-btn"
              aria-label="Continue with Google"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              Continue with Google
            </button>

            {/* Secondary sign-up / sign-in mode toggle */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 border-t border-slate-200 dark:border-slate-700" />
              <span className="text-xs text-text-muted">
                {mode === 'login' ? 'New to Bridgeway?' : 'Already have an account?'}
              </span>
              <div className="flex-1 border-t border-slate-200 dark:border-slate-700" />
            </div>

            <button
              type="button"
              onClick={() => {
                setMode(mode === 'login' ? 'signup' : 'login');
                setTouched({});
              }}
              className="w-full h-11 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-primary hover:text-primary dark:hover:border-primary text-text-primary text-sm font-medium transition-all duration-200"
              data-testid="toggle-mode-btn"
            >
              {mode === 'login' ? 'Create a Bridgeway account' : 'Sign in instead'}
            </button>
          </div>

          {/* De-emphasized back to home */}
          <div className="text-center mt-5">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-xs text-text-muted hover:text-text-secondary transition-colors"
              data-testid="back-home-btn"
            >
              ← {t('auth.back_home')}
            </button>
          </div>
        </div>
      </div>

      <HeroPanel />
    </div>
  );
};

export default AuthPage;
